import React, { Component } from "react";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { sumBy } from "@formant/core/common/sumBy";
import { IDeviceConfigurationTemplate } from "@formant/core/model/IDeviceConfigurationTemplate";

import { deviceConfigurationTemplateStore, routerStore } from "../../../stores";
import { localSettingObservable } from "../../../utils/localSettingObservable";
import {
    DataTable,
    defaultSimpleSortState,
    ISimpleSortState,
} from "../../common/DataTable";
import { IDataTableColumn } from "../../common/DataTable/IDataTableColumn";
import { Help } from "../../common/Help";
import { Scrollable } from "../../common/Scrollable";
import {
    applySimpleFilter,
    emptySimpleFilterState,
    ISimpleFilterState,
    SimpleFilter,
    tagDimensions,
} from "../../common/SimpleFilter";
import { StandardButton } from "../../common/StandardButton";
import { StatsHeader } from "../../common/StatsHeader";
import { RowDate } from "../RowDate";
import { RowTitle } from "../RowTitle";
import { SettingsPage } from "../SettingsPage";
import { Actions } from "./Actions";

@observer
export class DeviceConfigurationTemplates extends Component {
    @localSettingObservable("settings-config-templates-filter")
    private filter: ISimpleFilterState = emptySimpleFilterState;

    @localSettingObservable("settings-config-templates-sort")
    private sort: ISimpleSortState = defaultSimpleSortState;

    public render() {
        const { routeTo } = routerStore;
        const columns: IDataTableColumn<IDeviceConfigurationTemplate>[] = [
            {
                header: "Name",
                row: _ => <RowTitle title={_.name} icon="configuration" />,
                sortBy: _ => _.name,
            },
            {
                header: "Created",
                row: _ => <RowDate date={_.createdAt} />,
                sortBy: _ => _.createdAt,
                hideBelow: "md",
            },
            {
                header: "Updated",
                row: _ => <RowDate date={_.updatedAt} />,
                sortBy: _ => _.updatedAt,
                hideBelow: "md",
            },
            {
                header: "",
                row: _ => <Actions deviceConfigurationTemplate={_} />,
            },
        ];

        const deviceConfigurationTemplates =
            deviceConfigurationTemplateStore.list();
        const statHeaderGroups = [
            {
                subtotalQuantity: sumBy(
                    deviceConfigurationTemplates || [],
                    _ => _.document.telemetry?.streams?.length || 0,
                ),
                subtotalLabel: "Telemetry streams",
            },
            {
                subtotalQuantity: sumBy(
                    deviceConfigurationTemplates || [],
                    _ => _.document.teleop?.rosStreams?.length || 0,
                ),
                subtotalLabel: "ROS streams",
            },
            {
                subtotalQuantity: sumBy(
                    deviceConfigurationTemplates || [],
                    _ => _.document.teleop?.joysticks?.length || 0,
                ),
                subtotalLabel: "Joysticks",
            },
        ];

        const { filter, onChangeFilter, sort, onChangeSort } = this;
        const dimensions = [
            ...tagDimensions(deviceConfigurationTemplates || [], _ => _.tags),
        ];
        const filteredDeviceConfigurationTemplates = applySimpleFilter(
            deviceConfigurationTemplates || [],
            dimensions,
            getTemplateText,
            filter,
        );

        return (
            <SettingsPage
                title="Configuration"
                extra={
                    <Help
                        text="Configuration is a group of settings that you can apply across devices, individually or in bulk. It lets you ensure that multiple devices all share the same settings. You can create, edit, or delete configurations, and apply them to new or existing devices."
                        // to="https://docs.formant.io/99529-configure-devices" // TODO
                    />
                }
                actions={
                    <StandardButton
                        to={routeTo.createDeviceConfigurationTemplate()}
                    >
                        Add configuration
                    </StandardButton>
                }
            >
                <StatsHeader
                    icon="configuration"
                    loading={!deviceConfigurationTemplateStore}
                    totalQuantity={deviceConfigurationTemplates?.length || 0}
                    totalLabel="Configurations"
                    groups={statHeaderGroups}
                />
                <SimpleFilter
                    dimensions={dimensions}
                    rows={deviceConfigurationTemplates || []}
                    filter={filter}
                    onChangeFilter={onChangeFilter}
                />
                <Scrollable>
                    <DataTable
                        rows={filteredDeviceConfigurationTemplates}
                        columns={columns}
                        loading={deviceConfigurationTemplates === undefined}
                        noResultsText="No configurations found"
                        onRowClicked={this.onEdit}
                        sort={sort}
                        onChangeSort={onChangeSort}
                    />
                </Scrollable>
            </SettingsPage>
        );
    }

    private onChangeFilter = (filter: ISimpleFilterState) => {
        this.filter = filter;
    };

    private onChangeSort = (sort: ISimpleSortState) => {
        this.sort = sort;
    };

    private onEdit = async (template: IDeviceConfigurationTemplate) => {
        const { navigate, routeTo } = routerStore;
        await navigate(
            routeTo.editDeviceConfigurationTemplate(
                { id: defined(template.id) },
                { tab: "General" },
            ),
        );
    };
}

function getTemplateText(template: IDeviceConfigurationTemplate): string {
    return template.name;
}
